import { ArrowUpRight } from 'lucide-react';
import { useTranslation } from '../../../lib/i18n';
import { useBasePath } from '../../../lib/use-base-path';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useProductName } from '../../../lib/use-brand';
import { APP_SMART_LINK } from '../../../lib/app-store-links';
import { installLink } from '../../../lib/referral-share-link';
import { useStoredInvite } from '../../../lib/use-stored-invite';
import { Badge, IconButton } from '../ui';
import styles from './MobileAppBanner.module.css';

const PAGE = 'home';

// "Download Symphony mobile app" — the mobile home get-the-app banner (Figma 13918-214272), sitting
// under the morning brief. A lilac (--ds-accent-02) card box: a "Stay connected" eyebrow badge + a
// black arrow icon button on top, an Archivo display title, and a concentric-rings graphic that
// bleeds off the bottom. The install smart-link lives on the arrow icon button (Figma's ↗ affordance),
// whose stretched hit area covers the whole card — so the banner is tappable anywhere while the one
// accessible link sits on the arrow (no nested anchors). Shown only to an account the server confirms
// never had the app on a phone (useShowMobileAppCard).
//
// Web-only, deliberately: the banner exists to get the app onto a phone, and inside the app there is
// nothing left to ask for — the written-down "get the app" exception (see orion-web/CLAUDE.md). This
// replaces the QR `MobileAppCard` on mobile; desktop keeps that card (bare) in its ⅔/⅓ header row.
export function MobileAppBanner() {
  const { t } = useTranslation();
  const basePath = useBasePath();
  const productName = useProductName();
  const { logPageClickBi } = useGenericBi();
  // This banner IS the hand-off to a phone — it only renders on mobile web — so it has to carry the
  // invite, or the referral is lost at the tap that sends the invited user to the app.
  const invite = useStoredInvite();
  return (
    <div className={styles.banner}>
      {/* Header row + title share the 8px group rhythm; the group sits 24px above the rings (Figma). */}
      <div className={styles.top}>
        <div className={styles.head}>
          <Badge variant="agent-profession" label={t('home.mobileApp.eyebrow')} />
          {/* The arrow carries the smart-link; `.arrowLink` stretches its hit area over the whole card. */}
          <IconButton
            href={installLink(invite, APP_SMART_LINK)}
            target="_blank"
            rel="noreferrer"
            aria-label={t('home.mobileApp.title', { productName })}
            variant="contained"
            color="standard"
            size="tiny"
            className={styles.arrowLink}
            onClick={() => logPageClickBi({ page_name: PAGE, button_name: 'download_app' }, { componentName: 'download-app' })}
          >
            <ArrowUpRight size={16} aria-hidden />
          </IconButton>
        </div>
        <h2 className={styles.title}>{t('home.mobileApp.title', { productName })}</h2>
      </div>
      {/* Decorative brand illustration (Figma export) — a static asset so its brand colours never touch
          the token guard, exactly like the store badges. A negative bottom margin lets only its top arcs
          count toward the card height; `overflow: hidden` clips the rest so it bleeds off the bottom. */}
      <img src={`${basePath}/app-rings.svg`} alt="" aria-hidden className={styles.rings} width={227} height={227} />
    </div>
  );
}
